import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useData } from "@/context/data-provider";
import type { Apoio, ApoioTipo } from "@/types";
import { APOIO_LABELS, PRIO_LABELS, POT_LABELS } from "@/lib/constants";
import { toCsv, downloadCsv } from "@/lib/csv";

type Filter = "all" | ApoioTipo;

const emptyApoio = (): Omit<Apoio, "id"> => ({
  medicoNome: "", medicoId: null, evento: "", dataInicio: "", dataFim: "", tipo: "inscricao", notas: "",
});

export default function Apoios() {
  const {
    doctors: { doctors },
    apoios: { apoios, addApoio, updateApoio, deleteApoio },
    period: { period },
  } = useData();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [filterByPeriod, setFilterByPeriod] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Apoio, "id">>(emptyApoio());

  const filtered = useMemo(() => {
    return apoios.filter((a) => {
      const q = search.toLowerCase();
      const match = !q || a.medicoNome.toLowerCase().includes(q) || a.evento.toLowerCase().includes(q);
      if (!match) return false;
      if (filter !== "all" && a.tipo !== filter) return false;
      if (filterByPeriod) {
        // Overlap check: apoio overlaps with period
        if (a.dataFim < period.start || a.dataInicio > period.end) return false;
      }
      return true;
    });
  }, [apoios, search, filter, filterByPeriod, period]);

  const summary = useMemo(() => ({
    total: apoios.length,
    inscricao: apoios.filter((a) => a.tipo === "inscricao").length,
    inscricao_aloj: apoios.filter((a) => a.tipo === "inscricao_aloj").length,
    completo: apoios.filter((a) => a.tipo === "completo").length,
  }), [apoios]);

  const openAdd = () => { setEditingId(null); setForm(emptyApoio()); setModalOpen(true); };
  const openEdit = (a: Apoio) => { setEditingId(a.id); const { id, ...rest } = a; setForm(rest); setModalOpen(true); };
  const handleSave = () => {
    if (!form.medicoNome || !form.evento) return;
    if (editingId) updateApoio(editingId, form);
    else addApoio(form);
    setModalOpen(false);
  };
  const handleDocSelect = (id: string) => {
    const doc = doctors.find((d) => d.id === id);
    if (doc) setForm((f) => ({ ...f, medicoId: doc.id, medicoNome: doc.name }));
  };

  const exportCsv = () => {
    const headers = ["Médico", "Evento", "Tipo", "Início", "Fim", "Notas"];
    const rows = filtered.map((a) => [a.medicoNome, a.evento, APOIO_LABELS[a.tipo], a.dataInicio, a.dataFim, a.notas]);
    downloadCsv("apoios.csv", toCsv(headers, rows));
  };

  // Lookup doctor info for apoio cards
  const getDoc = (a: Apoio) => doctors.find((d) => d.id === a.medicoId);

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-[1.3rem]" style={{ color: "var(--mv-ink-dark)" }}>Apoios</h1>
        <div className="flex gap-2">
          {apoios.length > 0 && <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={exportCsv}>📥 CSV</button>}
          <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={openAdd}>+ Novo</button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-[10px] mb-[18px]">
        {([
          { title: "Total", num: summary.total, sub: "apoios" },
          { title: "Inscrição", num: summary.inscricao, sub: "apenas" },
          { title: "Insc. + Aloj.", num: summary.inscricao_aloj, sub: "" },
          { title: "Completo", num: summary.completo, sub: "" },
        ]).map((c) => (
          <div key={c.title} className="stat-card" style={{ borderTop: "none" }}>
            <div className="stat-label">{c.title}</div>
            <div className="stat-number">{c.num}</div>
            {c.sub && <div className="stat-sub">{c.sub}</div>}
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex gap-[7px] mb-[13px] flex-wrap items-center">
        <input className="mv-input flex-1 min-w-[140px]" placeholder="Pesquisar médico ou evento..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {([["all", "Todos"], ["inscricao", "Inscrição"], ["inscricao_aloj", "Insc.+Aloj."], ["completo", "Completo"]] as [Filter, string][]).map(([f, label]) => (
          <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>{label}</button>
        ))}
        <button
          className={`filter-btn ${filterByPeriod ? "active" : ""}`}
          onClick={() => setFilterByPeriod(!filterByPeriod)}
        >
          📅 Período
        </button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="mv-panel"><div className="empty-state"><div className="empty-icon">🤝</div>Nenhum apoio encontrado</div></div>
      ) : (
        <div className="flex flex-col gap-[10px]">
          {filtered.map((a) => {
            const doc = getDoc(a);
            return (
              <div key={a.id} className="apoio-card group">
                <div className="flex items-start gap-[10px] mb-[10px] flex-wrap">
                  <div className="font-serif text-[0.95rem] flex-1" style={{ color: "var(--mv-ink-dark)" }}>{a.evento}</div>
                  <span className={`apoio-tipo apoio-tipo-${a.tipo}`}>{APOIO_LABELS[a.tipo]}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={() => openEdit(a)}>✏</button>
                    <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={() => deleteApoio(a.id)}>🗑</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <div className="font-mono text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--mv-ink-light)" }}>Médico</div>
                    <div className="text-[0.8rem] font-semibold">{a.medicoNome}</div>
                    {doc && (
                      <div className="flex items-center gap-[6px] mt-1">
                        <span className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{doc.local}</span>
                        <span className={`ptag ptag-${doc.prio}`}>{PRIO_LABELS[doc.prio]}</span>
                        <span className={`pottag pottag-${doc.pot}`}>{POT_LABELS[doc.pot]}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-mono text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--mv-ink-light)" }}>Data</div>
                    <div className="text-[0.8rem] font-semibold">{a.dataInicio} — {a.dataFim}</div>
                  </div>
                  {a.notas && (
                    <div>
                      <div className="font-mono text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--mv-ink-light)" }}>Notas</div>
                      <div className="text-[0.76rem]" style={{ color: "var(--mv-ink-light)" }}>{a.notas}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalOpen && createPortal(
        <div
          className="fixed inset-0 z-[600] flex items-start justify-center"
          style={{ background: "rgba(15,46,90,0.5)", backdropFilter: "blur(3px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div
            className="mt-14 mx-4 w-full max-w-[600px] rounded-[16px] overflow-y-auto overflow-x-hidden max-h-[80vh]"
            style={{ background: "var(--mv-surface)", border: "1px solid var(--mv-border)", boxShadow: "0 20px 60px rgba(15,46,90,0.25)", animation: "modalIn 0.22s ease" }}
          >
            <div className="mv-panel-header sticky top-0 z-10 justify-between">
              <span className="mv-panel-title">{editingId ? "Editar Apoio" : "Novo Apoio"}</span>
              <button className="text-[1.15rem] opacity-40 hover:opacity-100" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div>
                <label className="mv-label">Médico</label>
                <select className="mv-select" value={form.medicoId ?? ""} onChange={(e) => handleDocSelect(e.target.value)}>
                  <option value="">— Selecionar —</option>
                  {doctors.map((d) => <option key={d.id} value={d.id}>{d.name} — {d.local}</option>)}
                </select>
              </div>
              <div><label className="mv-label">Evento</label><input className="mv-input" value={form.evento} onChange={(e) => setForm({ ...form, evento: e.target.value })} /></div>
              <div>
                <label className="mv-label">Tipo</label>
                <select className="mv-select" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as ApoioTipo })}>
                  <option value="inscricao">Inscrição</option>
                  <option value="inscricao_aloj">Insc. + Alojamento</option>
                  <option value="completo">Completo</option>
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 9 }}>
                <div>
                  <label className="mv-label">Início</label>
                  <input type="date" className="mv-input" value={form.dataInicio} onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} />
                </div>
                <div>
                  <label className="mv-label">Fim</label>
                  <input type="date" className="mv-input" value={form.dataFim} onChange={(e) => setForm({ ...form, dataFim: e.target.value })} />
                </div>
              </div>
              <div><label className="mv-label">Notas</label><textarea className="mv-textarea" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
            </div>
            <div className="p-[12px_20px] flex gap-2 justify-end" style={{ borderTop: "1px solid var(--mv-border)", background: "#FAFCFF" }}>
              <button className="mv-btn mv-btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="mv-btn mv-btn-primary" onClick={handleSave} disabled={!form.medicoNome || !form.evento}>{editingId ? "Guardar" : "Adicionar"}</button>
            </div>
          </div>

        </div>,
        document.body
      )}
    </div>
  );
}
